import type { PointerEvent as ReactPointerEvent } from 'react'
import { Coffee, UserMinus } from 'lucide-react'
import type { BoardSpan } from '../../../shared/lib/schedule'
import {
  LINE_PX,
  STRIP_ICON_MIN,
  WIDE_LINE_PX,
  distributeSegmentHeights,
  resolveWorkDensity,
  type WorkDensity,
} from '../model/blockDensity'
import type { BarBlockView, BlockView, JobBlockView } from '../model/blockView'
import { MIN_BLOCK_PX, spanHeightPx, spanTopPx } from '../model/calendarConstants'
import { BlockSegment, textFor } from './BlockSegment'
import { CalendarBlockShell } from './CalendarBlockShell'
import { TravelStrip } from './TravelStrip'
import { ReschedulingMarker, WorkBody } from './WorkBody'

/**
 * One entry on the worker calendar — Termin, Aufgabe, Pause and Abwesenheit all render through
 * here, the way the board's `ScheduleBlock` does. What differs between the kinds is data, and
 * `resolveBlockView` has already decided it; this component only knows two shapes: a job, which is
 * three strips, and a bar, which is one.
 *
 * Interaction is not decided here either. `DayColumn` owns the policy (who may drag what) and
 * hands down the handlers; the shell turns their presence into a button or a drag target.
 */

interface Props {
  view: BlockView
  /** The one-day view: a block has the full width, so it spends it on duration and contact. */
  wide?: boolean
  /** The occupied block from `schedule.startsAt`/`endsAt` — see `buildDayBlocks`. */
  span: BoardSpan
  left?: string
  zIndex?: number
  /** Tap, click or Enter. Absent on a block that opens nothing. */
  onActivate?: () => void
  onPointerDown?: (e: ReactPointerEvent<HTMLDivElement>) => void
  isDragging?: boolean
  /** Where a drag has moved the block to, in grid pixels — replaces the span's own top. */
  overrideTopPx?: number
}

export const WorkerScheduleBlock = ({
  view,
  wide = false,
  span,
  left,
  zIndex,
  onActivate,
  onPointerDown,
  isDragging,
  overrideTopPx,
}: Props) => {
  const top = overrideTopPx ?? spanTopPx(span)
  const height = spanHeightPx(span, MIN_BLOCK_PX)

  const label = view.layout === 'job' ? `${view.kindLabel} ${view.description}` : view.description

  return (
    <CalendarBlockShell
      topPx={top}
      heightPx={height}
      left={left}
      zIndex={zIndex}
      label={label}
      title={view.description}
      onActivate={onActivate}
      onPointerDown={onPointerDown}
      isDragging={isDragging}
      // Pause and Abwesenheit recede: they are the day's background, not the day's work.
      muted={view.layout === 'bar'}
      rescheduling={view.rescheduling}
    >
      {view.layout === 'job' ? (
        <JobStrips view={view} heightPx={height} wide={wide} />
      ) : (
        <BarBody view={view} heightPx={height} wide={wide} />
      )}
    </CalendarBlockShell>
  )
}

/** Anfahrt / Arbeit / Abfahrt, sized against the box so they can never add up to more than it. */
const JobStrips = ({
  view,
  heightPx,
  wide,
}: {
  view: JobBlockView
  heightPx: number
  wide: boolean
}) => {
  const [travelPx, workPx, departurePx] = distributeSegmentHeights(heightPx, view.minutes)
  const fit = resolveWorkDensity(workPx, wide ? WIDE_LINE_PX : LINE_PX)

  return (
    <>
      <TravelStrip
        kind={view.travelKind}
        color={view.travelColor}
        heightPx={travelPx}
        wide={wide}
        minutes={wide ? view.minutes[0] : undefined}
      />

      <BlockSegment
        color={view.workColor}
        heightPx={workPx}
        divided={travelPx > 0}
        ringed={view.ringed}
      >
        {/* `null`, not an element that renders nothing: a childless segment skips its scrim, and a
            block too short for text should keep its full colour rather than a darkened one. */}
        {fit.tier === 'bare' ? null : (
          <WorkBody
            fit={fit}
            wide={wide}
            workMinutes={view.minutes[1]}
            timeLabel={view.timeLabel}
            subject={view.subject}
            who={view.who}
            contact={view.contact}
            running={view.running}
            rescheduling={view.rescheduling}
            attachmentCount={view.attachmentCount}
          />
        )}
      </BlockSegment>

      <TravelStrip
        kind={view.departureKind}
        color={view.departureColor}
        heightPx={departurePx}
        divided
        wide={wide}
        minutes={wide ? view.minutes[2] : undefined}
      />
    </>
  )
}

const BAR_ICON = { coffee: Coffee, 'user-minus': UserMinus } as const

/**
 * One strip with an icon — the same ladder as a job's work segment. A short Pause keeps its cup
 * where it cannot keep the word, so the grid stays readable without the worker decoding grey.
 */
const BarBody = ({
  view,
  heightPx,
  wide,
}: {
  view: BarBlockView
  heightPx: number
  wide: boolean
}) => {
  const { tier } = resolveWorkDensity(heightPx, wide ? WIDE_LINE_PX : LINE_PX)
  const Icon = BAR_ICON[view.icon]
  const pill = view.rescheduling ? <ReschedulingMarker withWord={tier !== 'bare'} wide={wide} /> : null
  const icon = (
    <Icon className={wide ? 'h-3.5 w-3.5 shrink-0' : 'h-3 w-3 shrink-0'} strokeWidth={2.5} aria-hidden />
  )

  return (
    <BlockSegment color={view.color} heightPx={Math.round(heightPx)} padded={tier !== 'bare'}>
      {resolveBarContent(tier, heightPx, icon, view, pill, wide)}
    </BlockSegment>
  )
}

function resolveBarContent(
  tier: WorkDensity,
  heightPx: number,
  icon: JSX.Element,
  view: BarBlockView,
  pill: JSX.Element | null,
  wide: boolean,
) {
  const text = textFor(wide)
  if (tier === 'bare') return heightPx >= STRIP_ICON_MIN ? icon : null

  // The meta rides on the label's row when there is only one, and takes its own below that. A wide
  // block always has the width for both on one row.
  if (tier === 'single' || view.meta == null || wide) {
    return (
      <span className="flex min-w-0 items-center gap-1">
        {icon}
        <span className={text.subject}>{view.label}</span>
        {pill}
        {view.meta && (
          <span className={`${text.meta} ml-auto shrink-0 tabular-nums`}>{view.meta}</span>
        )}
      </span>
    )
  }

  return (
    <div className="flex h-full w-full min-w-0 flex-col justify-start">
      <span className="flex min-w-0 items-center gap-1">
        {icon}
        <span className={text.subject}>{view.label}</span>
        {pill}
      </span>
      <span className={`${text.meta} tabular-nums`}>{view.meta}</span>
    </div>
  )
}
